/** How CS2 shows its weapons, by the short names demos use ("m4a1" is the M4A4). */
const NAMES: Record<string, string> = {
  ak47: "AK-47",
  m4a1: "M4A4",
  m4a4: "M4A4",
  m4a1_silencer: "M4A1-S",
  m4a1_silencer_off: "M4A1-S",
  awp: "AWP",
  galilar: "Galil AR",
  famas: "FAMAS",
  aug: "AUG",
  sg556: "SG 553",
  ssg08: "SSG 08",
  scar20: "SCAR-20",
  g3sg1: "G3SG1",
  deagle: "Desert Eagle",
  revolver: "R8 Revolver",
  glock: "Glock-18",
  hkp2000: "P2000",
  usp_silencer: "USP-S",
  usp_silencer_off: "USP-S",
  p250: "P250",
  fiveseven: "Five-SeveN",
  tec9: "Tec-9",
  cz75a: "CZ75-Auto",
  elite: "Dual Berettas",
  mac10: "MAC-10",
  mp9: "MP9",
  mp7: "MP7",
  mp5sd: "MP5-SD",
  ump45: "UMP-45",
  p90: "P90",
  bizon: "PP-Bizon",
  nova: "Nova",
  xm1014: "XM1014",
  mag7: "MAG-7",
  sawedoff: "Sawed-Off",
  negev: "Negev",
  m249: "M249",
  taser: "Zeus x27",
  c4: "C4",
  planted_c4: "C4",
  hegrenade: "HE grenade",
  flashbang: "Flashbang",
  smokegrenade: "Smoke",
  molotov: "Molotov",
  incgrenade: "Incendiary",
  inferno: "Fire",
  decoy: "Decoy",
  world: "World",
};

/** "ak47" → "AK-47"; any knife → "Knife"; unknown names come back as they are. */
export function weaponLabel(name: string): string {
  if (!name) return "";
  const key = name.toLowerCase().replace(/^weapon_/, "");
  if (NAMES[key]) return NAMES[key];
  if (key.startsWith("knife") || key === "bayonet" || key.includes("knife")) return "Knife";
  return key;
}

/** CS2's item definition indexes (what the game and its purchase records use), by name as in the icons. */
const ITEM_DEFS: Record<number, string> = {
  1: "deagle", 2: "elite", 3: "fiveseven", 4: "glock", 7: "ak47", 8: "aug", 9: "awp", 10: "famas", 11: "g3sg1", 13: "galilar",
  14: "m249", 16: "m4a1", 17: "mac10", 19: "p90", 23: "mp5sd", 24: "ump45", 25: "xm1014", 26: "bizon", 27: "mag7", 28: "negev",
  29: "sawedoff", 30: "tec9", 31: "taser", 32: "hkp2000", 33: "mp7", 34: "mp9", 35: "nova", 36: "p250", 38: "scar20", 39: "sg556",
  40: "ssg08", 41: "knife", 42: "knife", 43: "flashbang", 44: "hegrenade", 45: "smokegrenade", 46: "molotov", 47: "decoy",
  48: "incgrenade", 49: "c4", 50: "kevlar", 51: "assaultsuit", 52: "heavy_armor", 55: "defuser", 57: "healthshot", 59: "knife_t",
  60: "m4a1_silencer", 61: "usp_silencer", 63: "cz75a", 64: "revolver",
};

/** A weapon's name from its item definition index; knife skins (500 and up) are "knife". */
export function itemName(def: number): string | undefined {
  if (ITEM_DEFS[def]) return ITEM_DEFS[def];
  if (def >= 500 && def < 600) return "knife";
  return undefined;
}

const PISTOLS = new Set(["deagle", "elite", "fiveseven", "glock", "hkp2000", "p250", "tec9", "cz75a", "revolver", "usp_silencer"]);
const GRENADES = ["hegrenade", "flashbang", "smokegrenade", "molotov", "incgrenade", "decoy"];

/** The kind of slot an item takes, as the game orders them: primary, pistol, knife, grenades, then the rest. */
export function itemSlot(name: string): "primary" | "pistol" | "knife" | "grenade" | "other" {
  const n = name.replace(/^weapon_/, "");
  if (PISTOLS.has(n)) return "pistol";
  if (n.startsWith("knife") || n === "bayonet") return "knife";
  if (GRENADES.includes(n)) return "grenade";
  if (["c4", "taser", "kevlar", "assaultsuit", "heavy_armor", "defuser", "healthshot", ""].includes(n)) return "other";
  return "primary";
}

/** Sort key: primary, pistol, knife, grenades in the game's order, the rest. */
export function itemOrder(name: string): number {
  const slot = itemSlot(name);
  if (slot === "grenade") return 30 + GRENADES.indexOf(name);
  return { primary: 0, pistol: 10, knife: 20, grenade: 30, other: 40 }[slot];
}
